import { cva, VariantProps } from 'class-variance-authority'
import { forwardRef } from 'react'

import { Button, ButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type ViverseButtonVariants = VariantProps<typeof buttonVariants>['variant']

type ViverseButtonSize = VariantProps<typeof buttonVariants>['size']

export type ViverseButtonProps = Omit<ButtonProps, 'size' | 'variant'> & {
  variant?: ViverseButtonVariants
  size?: ViverseButtonSize
}

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap disabled:opacity-100 disabled:bg-vive-ghost-white',
  {
    variants: {
      variant: {
        /** Blue Button */
        default:
          'rounded-full text-vive-white bg-vive-brand-50 hover:bg-vive-brand-40 active:bg-vive-brand-60',
        /** Outline Button */
        secondary:
          'rounded-full shadow-[0px_0px_0px_1.5px_var(--neutral-20)_inset] text-vive-primary-20 bg-transparent hover:bg-vive-primary-60 hover:text-vive-white hover:shadow-[0px_0px_0px_1.5px_var(--neutral-white)_inset] active:bg-transparent active:text-vive-primary-40 active:shadow-[0px_0px_0px_1.5px_var(--neutral-40)_inset]',
        /** Gradient Button */
        extra:
          'rounded-full transition-all text-vive-white btn-bg-extra hover:bg-right disabled:opacity-40',
        extra2:
          'rounded-full transition-all text-vive-primary-80 disabled:bg-none bg-gradient-btn hover:bg-gradient-btn-hover disabled:bg-vive-primary-80 disabled:text-vive-primary-60 active:bg-gradient-btn-active',
        extraOutline:
          'border-[1.5px] transition-all border-transparent rounded-full [background:linear-gradient(#000,#000)_padding-box,var(--background-image-gradient-btn)_border-box] hover:[background:linear-gradient(#000,#000)_padding-box,var(--background-image-gradient-btn-hover)_border-box] active:[background:linear-gradient(#000,#000)_padding-box,var(--background-image-gradient-btn-active)_border-box]',
        /** Filter Button */
        filter:
          'subheader-2 font-normal gap-[6px] ml:gap-[8px] rounded-full bg-vive-primary-70 text-vive-primary-20 ml:text-vive-primary-20 hover:bg-vive-primary-60 hover:text-vive-white',
        filterActive:
          'subheader-2 font-normal gap-[6px] ml:gap-[8px] rounded-full bg-vive-primary-70 text-primary shadow-[0px_0px_0px_1.8px_var(--primary)_inset] hover:bg-vive-primary-60 hover:text-vive-brand-40 hover:shadow-[0px_0px_0px_1.8px_var(--brand-vive-40)_inset] ml:text-primary',
        filterTag:
          'subheader-2 font-normal gap-[8px] ml:gap-[16px] rounded-full shadow-[0px_0px_0px_2px_var(--neutral-70)_inset] text-vive-primary-20 ml:text-vive-primary-20 bg-transparent hover:shadow-[0px_0px_0px_2px_var(--neutral-50)_inset]',
        colorful:
          'rounded-full text-vive-white btn-bg-colorful hover:bg-right transition-all duration-500',
        outline: 'rounded-full border border-vive-brand-50 text-white hover:border-vive-brand-40',
        icon: 'rounded-full bg-[#FFFFFF66] hover:bg-[#FFFFFF80] active:bg-[#FFFFFF80] disabled:bg-vive-ghost-white disabled:shadow-none',
        icon2:
          'rounded-full bg-vive-primary-50 hover:bg-vive-primary-40 active:bg-vive-primary-50 disabled:bg-vive-ghost-white disabled:shadow-none',
        icon3:
          'rounded-full text-vive-primary-20 bg-vive-ghost-white font-medium hover:bg-vive-ghost-white-hover disabled:bg-vive-ghost-white/50 disabled:shadow-none',
        silent:
          'rounded-full text-vive-primary-20 font-medium hover:bg-vive-ghost-white-hover disabled:bg-vive-ghost-white/50 disabled:shadow-none',
        custom: '',
      },
      size: {
        xLarge: 'h-12 px-[27px] py-3 text-lg font-bold',
        /** Under 1440: mw: 160px, p: 20px 6px / Above 1440: mw: 200px, p: 20px 10px. Eg. Landing - Accept invitation*/
        large:
          'min-w-[160px] text-lg px-[20px] py-[6px] 1.5xl:min-w-[200px] 1.5xl:py-[10px] button',
        /** Under 768: mw: 120px, p: 20px 6px / Above 768: mw: 160px, p: 20px 6px / Above 1440: mw: 200px, p: 20px 10px. Eg. Landing - View all */
        medium: 'min-w-[120px] py-3 px-5 button text-[18px] leading-6',
        small2: 'px-[16px] py-[4px] button text-[14px] leading-[22px]',
        small:
          'min-w-[120px] px-[20px] py-[6px] md:min-w-[160px] 1.5xl:min-w-[200px] 1.5xl:py-[10px] button',
        /** Under 768: p: 10px 28px / Above 1440: p: 13px 28px. Eg. Landing MKTBanner - Learn more */
        extra:
          'px-7 leading-10 h-10 1.5xl:leading-[56px] 1.5xl:h-[56px] font-bold text-lg 1.5xl:text-[26px] button',
        fit: 'w-fit min-w-[120px] px-[20px] py-[6px] button',
        filter: 'w-fit px-[16px] py-[4px] ml:px-[26px] ml:py-[8px] button',
        filterTag: 'w-fit px-[16px] py-[4px] ml:px-[24px] ml:py-[6px] button',
        filterCategory: 'w-fit px-[12px] py-[5px] button',
        icon: 'size-10',
        custom: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'fit',
    },
  },
)

const ViverseButton = forwardRef<HTMLButtonElement, ViverseButtonProps>(function ViverseButton(
  { children, className, variant = 'default', size = 'fit', ...rest },
  ref,
) {
  return (
    <Button
      ref={ref}
      variant="ghost"
      size="custom"
      className={cn(buttonVariants({ variant, size, className }))}
      type="button"
      {...rest}
    >
      {children}
    </Button>
  )
})

export default ViverseButton
